import React, { useEffect, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import ChartFilter from './ChartFilter.jsx';
import Spinners from '../Spinner.jsx';
import { isEqual } from "lodash";
import PropTypes from "prop-types";

const LineDashedChart = (props) => {
  const {
    color = [],
    dashed = false,
    dashedItemName = "",
    data = [],
    enableRenderItemName = "",
    events = {},
    GoLiveDate = [],
    header = [],
    height = '400px',
    isChartWithFilter = false,
    isDahsedlineValid = false,
    isScrollEndLimit = false,
    isScrollEndLimitvertical = 100,
    isScrollStartLimit = false,
    isScrollStartLimitvertical = 0,
    IsChartMultiple = false,
    markLine = false,
    markPointer = false,
    markPointValue = "",
    markPoitsData = [],
    PointArea = [],
    scrollBarBottomAlign = 15,
    smooth = false,
    spaceBottom = "10%",
    spaceLeft = "2%",
    spaceRight = "2%",
    spaceTop = "15%",
    tooltipshow = true,
    toolTipConfig = {},
    width = "",
    xAxisGrid,
    xAxisLabelGap = 30,
    yAxisGrid,
    yAxeslabel = "",
    yAxisLabelGap,
    validateMessage,
    hasData,
  } = props;

  const chartRef = useRef(null);

  const [chartOptions, setChartOptions] = useState({});
  const [loading, setLoading] = useState(false);
  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };
  const previousData = usePrevious(data);

  useEffect(() => {
    if (!isEqual(previousData, data)) {
      const maxvalue2 = data.length > 0 ? data[0].awsApplicationBudget : "";
      let maxData = 0;

      for (const entry of data) {
        if (entry.totalCost > maxData) {
          maxData = entry.totalCost;
        }
      }
      const ymaxvalue = (Math.round(Math.max(maxData, maxvalue2) / 1000) * 1000000) + 1000;

      const formatYAxis = (value) => {
        const abs = Math.abs(value);

        if (abs >= 1_000_000_000_000) {
          return `${(value / 1_000_000_000_000).toFixed(1).replace(/\.0$/, '')}T`;
        }
        if (abs >= 1_000_000_000) {
          return `${(value / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
        }
        if (abs >= 1_000_000) {
          return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
        }
        if (abs >= 1_000) {
          return `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
        }

        return Math.round(value).toString();
      };

      // const flattenedData = data.flat();
      const formattedData = data.map((item) => ({
        month: item.month,
        totalCost: formatYAxis(Math.floor(item.totalCost)),
        budgetCost: formatYAxis(Math.floor(item.budgetCost)),
      }));

      const maxDigits1 = Math.max(
        ...formattedData.map((value) => value.toString().length)
      );

      const nameGapYAxis = (maxDigits) => {
        let nameGap = 22; // Default value for 2-digit numbers
        if (maxDigits === 3) { nameGap = 40; }
        else if (maxDigits === 4) { nameGap = 45; }
        else if (maxDigits === 5) { nameGap = 48; }
        else if (maxDigits === 6) { nameGap = 50; }
        else if (maxDigits === 7) { nameGap = 52; }
        return nameGap;
      };

      const countDigits = (num) => {
        return Math.max(Math.floor(Math.log10(Math.abs(num))), 0) + 1;
      };

      const maxDigits = countDigits(ymaxvalue);
      const yAxisLabelWidth = maxDigits * 10;

      const getLineOption = () => {
        const xAxisData = header
          .filter((field) => field.label === true)
          .map((field) => data.map((item) => item[field.value]));
        const act = data.map((item) => item.totalCost);

        const today = new Date();
        const options1 = { month: 'short' };
        const currentMonth = today.toLocaleString('default', options1);
        const currentMonthYear = `${currentMonth} ${today.getFullYear() % 100}`;
        const currentIndex = xAxisData[0].indexOf(currentMonthYear);

        const xAxis = xAxisData[0].slice(0, currentIndex + 1);

        const series = header.map((hitem, index) => {
          const markPointData = markPoitsData.filter((point) => point.name === hitem.value && point.value !== null && point.value !== '' && point.coord && point.coord.length > 0);
          const seriesItem = {
            name: hitem.text,
            symbolSize: 3,
            type: "line",
            step: hitem.step ? hitem.step : null,
            smooth: smooth,
            data: enableRenderItemName === hitem.text
              ? data
                .map((item, index) => index <= currentIndex ? [index, item[hitem.value]] : item.currentMonth === 0 ? null : item[hitem.value])
                .filter(item => hitem.text === "Budgeted Cost" ? item !== null && item !== undefined : true) // Apply filter conditionally
              : data
                .map((item) => item[hitem.value])
                .filter(value => hitem.text === "Budgeted Cost" ? value !== null && value !== undefined : true), // Apply filter conditionally
            lineStyle: {
              color: hitem.color,
            },
            itemStyle: {
              color: hitem.color,
            },
            z: 100,
            connectNulls: false,

            markPoint: (markPointer === true && PointArea.includes(hitem.value) && Array.isArray(markPointData) && markPointData.length > 0) ? {
              data: markPointData.flatMap((point) => {
                const coords = Array.isArray(point.coord[0]) ? point.coord : [point.coord];

                return coords.map((coord) => ({
                  coord,
                  name: point.name,
                  seriesName: point.seriesName,
                  value: point.value,
                  itemStyle: {
                    color: point.color,
                  },
                  label: {
                    formatter: () => markPointValue,
                    show: false,
                  },
                  symbol: "pin",
                  symbolSize: 28,
                }));
              }),
            } : null,
            markLine: ((GoLiveDate !== 0) && (markLine === true)) ? {
              symbol: ['pin'],
              label: {
                // rotate: 90,
                // position: "middle",
                formatter: (val) => {
                  const [label, date] = val.name.split(' - ');
                  return val.name; // Using <br/> for line break
                },
                show: true
              },
              data: GoLiveDate && GoLiveDate.map((date) => ({
                xAxis: date.xAxis,
                name: date.name,
                label: {
                  rotate: date ? date?.angle : 0,
                  position: date.position ? date.position : "middle",
                },
                lineStyle: {
                  color: date.color, // Set the line color dynamically based on GoLiveDate data
                  type: 'dashed' // You can also use 'dotted' or 'solid'
                }
              })),
              z: 0,
            } : {
              symbol: ['pin'],
              label: {
                formatter: (val) => {
                  const [label, date] = val.name.split(' - ');
                  return val.name; // Using <br/> for line break
                },
                show: true
              },
              data: GoLiveDate && GoLiveDate.map((date) => ({
                xAxis: date.xAxis,
                name: date.name,
                label: {
                  rotate: date ? date?.angle : 0,
                  position: date.position ? date.position : "middle",
                },
                lineStyle: {
                  color: date.color, // Set the line color dynamically based on GoLiveDate data
                  type: 'dashed' // You can also use 'dotted' or 'solid'
                }
              }))
            },
          };
          return seriesItem;
        });

        if (dashed) {
          const dashedSeries = header
            .filter((hitem) => hitem.text === enableRenderItemName)
            .map((hitem) => {
              return {
                name: dashedItemName,
                valName: hitem.text,
                symbolSize: 3,
                data: data.map((item, index) =>
                  index >= currentIndex ? [index, item[hitem.value]] : null
                ),
                type: "line",
                // step: "start",
                lineStyle: {
                  color: isDahsedlineValid === true ? "#47aa56" : hitem.color,
                  type: isDahsedlineValid === true ? "dashed" : hitem.type,
                },
                itemStyle: {
                  color: isDahsedlineValid === true ? "#47aa56" : hitem.color,
                },
                z: 5,
              };
            });

          series.push(...dashedSeries);
        }


        let options = {
          tooltip: {
            show: tooltipshow,
            trigger: "item",
            ...toolTipConfig,
          },
          grid: {
            top: spaceTop,
            left: spaceLeft,
            right: spaceRight,
            bottom: spaceBottom,
            containLabel: true,
          },
          xAxis: {
            type: "category",
            name: "Month",
            nameLocation: "middle",
            nameGap: xAxisLabelGap,
            data: xAxisData[0],
            boundaryGap: false,
            splitLine: {
              show: xAxisGrid !== undefined ? xAxisGrid : true,
            },
          },
          yAxis: {
            axisTick: {
              inside: false,
            },
            // name: yAxeslabel ? yAxeslabel : "",
            nameLocation: "middle",
            nameGap: yAxisLabelGap ? yAxisLabelGap : yAxisLabelWidth,
            splitLine: {
              show: yAxisGrid !== undefined ? yAxisGrid : false,
            },
            // max: ymaxvalue,
            axisLabel: {
              formatter: (value) => formatYAxis(value),
            },
          },
          legend: {
            data: header.filter((filter) => !filter.label).map((item) => item.text),
            icon: "circle",
            show: true,
            type: "scroll",
            orient: "horizontal",
            left: "left",
          },
          series, // Use the modified series array
        };
        if (props.selectFYFilter === "SelectedCustomYear" ? true : false) {
          options.dataZoom = options.dataZoom || [];
          options.dataZoom.push(
            {
              type: "slider",
              startValue: isScrollStartLimit,
              endValue: isScrollEndLimit,
              height: 15,
              bottom: scrollBarBottomAlign,
              showDetail: false,
              handleStyle: {
                color: color[0],
              },
              dataBackground: {
                lineStyle: {
                  color: color[0],
                },
                areaStyle: {
                  color: "rgba(255, 0, 0, 0.1)",
                },
              },
            },
            {
              type: "inside",
              start: 0,
              end: 30,
            }
          );
        }

        if (props.selectFYFilter === "SelectedCustomYear" ? true : false) {
          options.dataZoom = options.dataZoom || [];
          options.dataZoom.push(
            {
              type: "slider",
              start: isScrollStartLimitvertical,
              end: isScrollEndLimitvertical,
              width: 10,
              showDetail: false,
              yAxisIndex: 0,
              right: "0%",
              handleStyle: {
                color: "#00a3ff",
              },
              dataBackground: {
                lineStyle: {
                  color: "#00a3ff",
                },
                areaStyle: {
                  color: "rgba(255, 0, 0, 0.1)",
                },
              },
              orient: "vertical",
            }
          );
        }
        return options;
      }; setLoading(true);
      setChartOptions(getLineOption());

      const timer = setTimeout(() => {
        setLoading(false);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [
    data,
    props.selectFYFilterUpdate,
  ]);



  //chart data select
  const handleChartClick = (params) => { };

  // Legend select event handler
  const handleLegendSelectChanged = (params) => {
    props.handleLegendSelectChanged(params, chartRef);
  };

  const onEvents = {
    click: handleChartClick, //chart click event
    legendselectchanged: handleLegendSelectChanged, //legend click event
  };

  const handleSelectFY = (option) => {
    props.handleSelectFY(option);

  };
  const handleCustomFY = (option) => {
    props.handleCustomFY(option);
  };

  return (
    <div>
      {isChartWithFilter && (
        <ChartFilter
          handleSelectFY={handleSelectFY}
          currentFY={props.currentFY}
          getFinancialYearsData={props.getFinancialYearsData}
          getFinancialYearsData1={props.getFinancialYearsData1}
          ApplicationChange={props.ApplicationChange}
          handleCustomFY={handleCustomFY}
        />
      )}

      {loading ? (
        <Spinners />
      ) : hasData ? (
        <>
          <div className={!IsChartMultiple ? "yAxispositioningLinechart" : "yAxispositioningLinedashedchart"}>
            {yAxeslabel}
          </div>

          <ReactECharts
            ref={chartRef}
            option={chartOptions}
            onEvents={events ? onEvents : ""}
            style={{ height: height, width: width }}
          />
        </>
      ) : (
        <div className="NodataAvailableVMTDD NodataAvailable">
          {validateMessage}
        </div>
      )}
    </div>
  );
};
LineDashedChart.propTypes = {
  color: PropTypes.array,
  dashed: PropTypes.bool,
  dashedItemName: PropTypes.string,
  data: PropTypes.array,
  enableRenderItemName: PropTypes.string,
  events: PropTypes.object,
  GoLiveDate: PropTypes.array,
  header: PropTypes.array,
  height: PropTypes.string,
  isChartWithFilter: PropTypes.bool,
  isDahsedlineValid: PropTypes.bool,
  isScrollEndLimit: PropTypes.bool,
  isScrollEndLimitvertical: PropTypes.number,
  isScrollStartLimit: PropTypes.bool,
  isScrollStartLimitvertical: PropTypes.number,
  IsChartMultiple: PropTypes.bool,
  markLine: PropTypes.bool,
  markPointer: PropTypes.bool,
  markPointValue: PropTypes.string,
  markPoitsData: PropTypes.array,
  PointArea: PropTypes.array,
  scrollBarBottomAlign: PropTypes.number,
  smooth: PropTypes.bool,
  spaceBottom: PropTypes.string,
  spaceLeft: PropTypes.string,
  spaceRight: PropTypes.string,
  spaceTop: PropTypes.string,
  tooltipshow: PropTypes.bool,
  toolTipConfig: PropTypes.object,
  width: PropTypes.string,
  xAxisGrid: PropTypes.bool,
  xAxisLabelGap: PropTypes.number,
  yAxisGrid: PropTypes.bool,
  yAxeslabel: PropTypes.string,
  yAxisLabelGap: PropTypes.number,
  handleSelectFY: PropTypes.func.isRequired,
  handleCustomFY: PropTypes.func.isRequired,
  currentFY: PropTypes.string.isRequired,
  getFinancialYearsData: PropTypes.func.isRequired,
  getFinancialYearsData1: PropTypes.func.isRequired,
  ApplicationChange: PropTypes.func.isRequired,
  selectFYFilter: PropTypes.any,
  selectFYFilterUpdate: PropTypes.func,
  validateMessage: PropTypes.string,
};

export default LineDashedChart;


